import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API, BACKEND_URL } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Building2, MapPin, LogOut, User, Settings, Users, Trash2, Upload, Image as ImageIcon, X, Filter, Shield, Loader2, Mail, AlertTriangle, ArrowUpDown, Grid2x2, Grid3x3, List } from 'lucide-react';
import Logo from '@/components/Logo';
import Header from '@/components/Header';
import ImageCropModal from '@/components/ImageCropModal';
import PDFPageSelector from '@/components/PDFPageSelector';
import ConfirmationModal from '@/components/ConfirmationModal';
import { ProjectCardSkeletonGrid } from '@/components/ProjectCardSkeleton';
import { getProjectImageUrl, getProjectPlaceholderUrl } from '@/utils/projectImage';
import { isEmailVerified, PROJECT_ACCESS_VERIFICATION_MESSAGE, PROJECT_CREATE_VERIFICATION_MESSAGE } from '@/utils/emailVerification';

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingProject, setCreatingProject] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showDeleteProjectDialog, setShowDeleteProjectDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [newProject, setNewProject] = useState({ name: '', address: '' });
  const [profileData, setProfileData] = useState({ firstName: '', lastName: '', email: '', currentPassword: '', newPassword: '', confirmNewPassword: '' });
  const [subUsers, setSubUsers] = useState([]);
  const [newSubUser, setNewSubUser] = useState({ email: '' });
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [selectedUserIndex, setSelectedUserIndex] = useState(-1);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [companyData, setCompanyData] = useState({ 
    companyName: '', 
    companyType: '',
    description: '',
    headquartersAddress: {
      street: '',
      state: '',
      city: '',
      zipCode: ''
    },
    email: '',
    website: '',
    phone: '',
    fax: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [companyProjectSearch, setCompanyProjectSearch] = useState('');
  const [sharedProjectSearch, setSharedProjectSearch] = useState('');
  const [companyProjectSort, setCompanyProjectSort] = useState('updated_desc');
  const [sharedProjectSort, setSharedProjectSort] = useState('updated_desc');
  const [sharedProjectFilter, setSharedProjectFilter] = useState('all');
  const [resendingVerify, setResendingVerify] = useState(false);
  
  // Confirmation Modals
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  
  // Image upload states
  const [projectImageFile, setProjectImageFile] = useState(null);
  const [projectImagePreview, setProjectImagePreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [showPDFSelector, setShowPDFSelector] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('specard_view_mode') || 'standard');
  const projectImageInputRef = useRef(null);
  
  const { user, logout, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const emailVerified = isEmailVerified(user);

  // Verification may have completed elsewhere (another tab / email client), so
  // revalidate against the server before blocking project creation.
  const ensureVerifiedForProjectCreate = async () => {
    if (emailVerified) return true;
    const fresh = await refreshUser?.();
    if (isEmailVerified(fresh || user)) return true;
    toast.warning(PROJECT_CREATE_VERIFICATION_MESSAGE);
    return false;
  };

  const openProject = (projectId) => {
    if (!emailVerified) {
      toast.warning(PROJECT_ACCESS_VERIFICATION_MESSAGE);
      return;
    }
    navigate(`/project/${projectId}`);
  };

  // Helper function to get project image URL (supports both legacy and Cloudinary)
  const getImageUrl = (project, size = 'medium') => getProjectImageUrl(project, size, API);

  useEffect(() => {
    fetchProjects();
    fetchSubUsers();
    loadStates();
    if (user) {
      setProfileData({ 
        firstName: user.firstName || '', 
        lastName: user.lastName || '', 
        email: user.email || '', 
        currentPassword: '', 
        newPassword: '',
        confirmNewPassword: ''
      });
      loadCompanyData();
    }
  }, [user]);

  const loadStates = async () => {
    try {
      const response = await axios.get(`${API}/states`);
      setStates(response.data.states || []);
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadCities = async (stateCode) => {
    try {
      const response = await axios.get(`${API}/cities/${stateCode}`);
      setCities(response.data.cities || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadCompanyData = async () => {
    if (!user?.companyId) return;
    
    try {
      const response = await axios.get(`${API}/users/company`);
      const company = response.data;
      setCompanyData({
        companyName: company.companyName || '',
        companyType: company.companyType || '',
        description: company.description || '',
        headquartersAddress: company.headquartersAddress || {
          street: '',
          state: '',
          city: '',
          zipCode: ''
        },
        email: company.email || '',
        website: company.website || '',
        phone: company.phone || '',
        fax: company.fax || ''
      });
      // Set logo preview URL - check Cloudinary first, then legacy
      if (company.logoCloudinary?.urls?.medium) {
        setLogoPreview(company.logoCloudinary.urls.medium);
      } else if (company.logo) {
        setLogoPreview(`${BACKEND_URL}${company.logo}`);
      } else {
        setLogoPreview(null);
      }
      
      // Load cities if state is set
      if (company.headquartersAddress?.state) {
        loadCities(company.headquartersAddress.state);
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      // console.error('Failed to load projects:', error);
      // toast.error('Failed to load projects');
      console.error('Failed to load projects:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 401) {
    // Session expired — global interceptor handles logout, don't show duplicate toast
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to load projects');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/sub-users`);
      setSubUsers(response.data);
    } catch (error) {
      console.error('Failed to load sub-users');
    }
  };

  const processProjectImageFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and PDF files are allowed');
      return;
    }
    if (file.type === 'application/pdf') {
      setProjectImageFile(file);
      setShowPDFSelector(true);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setImageToCrop(event.target.result);
      setProjectImageFile(file);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleProjectImageSelect = (e) => {
    processProjectImageFile(e.target.files[0]);
  };

  const handleProjectImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    processProjectImageFile(file);
  };

  const handleProjectImagePaste = (e) => {
    if (showCropModal || showPDFSelector || creatingProject) return;
    if (projectImagePreview) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        e.stopPropagation();
        const file = item.getAsFile();
        processProjectImageFile(file);
        return;
      }
    }
  };

  // Document-level paste listener when Create Project dialog is open
  useEffect(() => {
    if (!showCreateDialog || projectImagePreview || showCropModal || showPDFSelector || creatingProject) return;
    const onDocumentPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          processProjectImageFile(item.getAsFile());
          return;
        }
      }
    };
    document.addEventListener('paste', onDocumentPaste);
    return () => document.removeEventListener('paste', onDocumentPaste);
  }, [showCreateDialog, projectImagePreview, showCropModal, showPDFSelector, creatingProject]);

  const handlePDFPageSelect = (imageDataUrl, pageNumber) => {
    setImageToCrop(imageDataUrl);
    setShowPDFSelector(false);
    setShowCropModal(true);
  };

  const handleCropComplete = (croppedBlob) => {
    setCroppedImageBlob(croppedBlob);
    setProjectImagePreview(URL.createObjectURL(croppedBlob));
    setShowCropModal(false);
    setImageToCrop(null);
    setProjectImageFile(null);
    if (projectImageInputRef.current) projectImageInputRef.current.value = '';
  };

  const removeProjectImage = () => {
    setProjectImageFile(null);
    setProjectImagePreview(null);
    setCroppedImageBlob(null);
    setImageToCrop(null);
    if (projectImageInputRef.current) projectImageInputRef.current.value = '';
  };

  const handleCropModalClose = () => {
    setShowCropModal(false);
    setImageToCrop(null);
    setProjectImageFile(null);
    if (projectImageInputRef.current) projectImageInputRef.current.value = '';
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (creatingProject) return;
    
    setCreatingProject(true);
    try {
      const formData = new FormData();
      formData.append('name', newProject.name);
      formData.append('address', newProject.address);
      
      // Add cropped image if available
      if (croppedImageBlob) {
        formData.append('file', croppedImageBlob, 'project-image.jpg');
      }

      const response = await axios.post(`${API}/projects`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Project created successfully');
      setShowCreateDialog(false);
      setNewProject({ name: '', address: '' });
      removeProjectImage();
      
      // Navigate to project detail page
      if (response.data && response.data.id) {
        navigate(`/project/${response.data.id}`);
      } else {
        fetchProjects();
      }
    } catch (error) {
      // toast.error('Failed to create project');
      const detail = error.response?.data?.detail;
      if (detail?.code === 'EMAIL_NOT_VERIFIED') {
        toast.error(detail.message || PROJECT_CREATE_VERIFICATION_MESSAGE);
        refreshUser?.();
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to create project');
      }
    } finally {
      setCreatingProject(false);
    }
  };

  const confirmDeleteProject = (project, e) => {
    e.stopPropagation(); // Prevent card click navigation
    if (!emailVerified) {
      toast.warning(PROJECT_ACCESS_VERIFICATION_MESSAGE);
      return;
    }
    setProjectToDelete(project);
    setShowDeleteProjectDialog(true);
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    
    try {
      await axios.delete(`${API}/projects/${projectToDelete.id}`);
      toast.success('Project deleted successfully');
      fetchProjects();
      setShowDeleteProjectDialog(false);
      setProjectToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (profileData.newPassword && profileData.newPassword !== profileData.confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      const updateData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email
      };
      
      // Only include password if provided
      if (profileData.currentPassword && profileData.newPassword) {
        updateData.currentPassword = profileData.currentPassword;
        updateData.newPassword = profileData.newPassword;
      }
      
      await axios.put(`${API}/users/profile`, updateData);
      toast.success('Profile updated successfully');
      setShowProfileDialog(false);
      setProfileData({ ...profileData, currentPassword: '', newPassword: '', confirmNewPassword: '' });
      
      // Update user context
      const updatedUser = { 
        ...user, 
        firstName: profileData.firstName, 
        lastName: profileData.lastName, 
        name: `${profileData.firstName} ${profileData.lastName}`,
        email: profileData.email 
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.location.reload(); // Refresh to update context
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleSearchUsers = async (query) => {
    if (!query || query.length < 2) {
      setUserSuggestions([]);
      setSelectedUserIndex(-1);
      return;
    }
    
    try {
      setSearchingUsers(true);
      const response = await axios.get(`${API}/users/search?query=${query}`);
      setUserSuggestions(response.data);
      setSelectedUserIndex(-1);
    } catch (error) {
      console.error('User search failed:', error);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleUserKeyDown = (e) => {
    if (!userSuggestions || userSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedUserIndex(prev => 
          prev < userSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedUserIndex(prev => 
          prev > 0 ? prev - 1 : userSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedUserIndex >= 0 && selectedUserIndex < userSuggestions.length) {
          const selectedUser = userSuggestions[selectedUserIndex];
          setNewSubUser({ email: selectedUser.email });
          setUserSuggestions([]);
          setSelectedUserIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setUserSuggestions([]);
        setSelectedUserIndex(-1);
        break;
    }
  };

  const handleCreateSubUser = async (e) => {
    e.preventDefault();
    
    if (!newSubUser.email) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      // Share only projects created by current user, not shared projects
      const ownProjects = projects.filter(p => p.createdBy === user?.id);
      
      if (ownProjects.length === 0) {
        toast.error('You have no projects to share');
        return;
      }
      
      const sharePromises = ownProjects.map(project => 
        axios.post(`${API}/projects/${project.id}/share`, { email: newSubUser.email })
      );
      
      const results = await Promise.allSettled(sharePromises);
      
      // Check if any succeeded
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;
      
      if (successCount > 0) {
        const firstSuccess = results.find(r => r.status === 'fulfilled');
        const responseData = firstSuccess.value.data;
        
        if (responseData.userExists) {
          toast.success(`Shared ${successCount} project(s) with ${responseData.userName}`);
        } else if (responseData.emailSent) {
          toast.success(`Invitation email sent to ${newSubUser.email}`);
        }
      }
      
      if (failCount > 0) {
        toast.warning(`${failCount} project(s) couldn't be shared`);
      }
      
      setNewSubUser({ email: '' });
      setUserSuggestions([]);
      fetchProjects(); // Refresh to show updated projects
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share projects');
    }
  };

  const handleDeleteSubUser = (subUser) => {
    setUserToRemove(subUser);
    setShowRemoveUserModal(true);
  };
  
  const confirmRemoveUser = async () => {
    if (!userToRemove) return;
    try {
      await axios.delete(`${API}/users/sub-users/${userToRemove.id}`);
      toast.success('Team member removed');
      fetchSubUsers();
    } catch (error) {
      toast.error('Failed to remove team member');
    } finally {
      setUserToRemove(null);
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      // Upload logo if provided
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        await axios.post(`${API}/upload/company-logo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      // Update company info
      await axios.put(`${API}/users/company`, companyData);
      toast.success('Company settings updated successfully');
      setShowCompanyDialog(false);
      setLogoFile(null);
      
      // Reload company data
      await loadCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company settings');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Sort helper function
  const sortProjects = (projectList, sortKey) => {
    const sorted = [...projectList];
    switch (sortKey) {
      case 'name_asc': return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case 'name_desc': return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      case 'created_asc': return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'created_desc': return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'updated_asc': return sorted.sort((a, b) => new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt));
      case 'updated_desc': return sorted.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
      default: return sorted;
    }
  };

  // Categorize projects: owned vs shared (before any search filter)
  const allOwnedProjects = useMemo(() =>
    projects.filter(project =>
      project.createdBy === user?.id ||
      (project.companyId && user?.companyId && project.companyId === user.companyId)
    ), [projects, user?.id, user?.companyId]);

  const allSharedProjects = useMemo(() =>
    projects.filter(project => {
      const isOwned = project.createdBy === user?.id ||
                      (project.companyId && user?.companyId && project.companyId === user.companyId);
      return !isOwned;
    }), [projects, user?.id, user?.companyId]);

  // Filter and sort company projects
  const ownedProjects = useMemo(() => {
    let filtered = allOwnedProjects;
    if (companyProjectSearch) {
      const s = companyProjectSearch.toLowerCase();
      filtered = filtered.filter(p => p.name.toLowerCase().includes(s) || p.address.toLowerCase().includes(s));
    }
    return sortProjects(filtered, companyProjectSort);
  }, [allOwnedProjects, companyProjectSearch, companyProjectSort]);

  // Filter and sort shared projects
  const sharedProjects = useMemo(() => {
    let filtered = allSharedProjects;
    if (sharedProjectSearch) {
      const s = sharedProjectSearch.toLowerCase();
      filtered = filtered.filter(p => p.name.toLowerCase().includes(s) || p.address.toLowerCase().includes(s));
    }
    return sortProjects(filtered, sharedProjectSort);
  }, [allSharedProjects, sharedProjectSearch, sharedProjectSort]);

  // Get distinct list of companies that shared projects (for shared projects filter)
  const sharedProjectCompanies = useMemo(() => {
    const companiesMap = new Map();
    sharedProjects.forEach(project => {
      // Get company info from project
      const companyId = project.companyId;
      const companyName = project.companyName || project.users?.find(u => u.userId === project.createdBy)?.companyName || 'Unknown Company';
      
      if (companyId && !companiesMap.has(companyId)) {
        companiesMap.set(companyId, {
          id: companyId,
          name: companyName
        });
      }
    });
    return Array.from(companiesMap.values());
  }, [sharedProjects]);

  // Filter shared projects by selected company
  const filteredSharedProjects = useMemo(() => {
    if (sharedProjectFilter === 'all') {
      return sharedProjects;
    }
    return sharedProjects.filter(project => project.companyId === sharedProjectFilter);
  }, [sharedProjects, sharedProjectFilter]);

  const getRoleBadge = (role) => {
    const badges = {
      owner: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      user: 'bg-green-100 text-green-800'
    };
    const labels = {
      owner: 'Owner',
      admin: 'Admin',
      user: 'User'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badges[role] || 'bg-gray-100 text-gray-800'}`}>
        {labels[role] || role}
      </span>
    );
  };

  const handleResendVerification = async () => {
    setResendingVerify(true);
    try {
      const res = await axios.post(`${API}/auth/send-verify-email`, {});
      if (res.data.alreadyVerified) {
        toast.info('Your email is already verified.');
      } else {
        toast.success('Verification email sent. Check your inbox.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        toast.error('Too many requests. Please try again later.');
      } else if (err.response?.status === 409 && detail?.code === 'DELIVERABILITY_BLOCKED') {
        toast.error('Cannot send verification email due to a delivery issue with your address. Please contact support.');
      } else {
        toast.error('Failed to send verification email.');
      }
    } finally {
      setResendingVerify(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b border-slate-200 sticky top-0 z-30" style={{ background: '#FF6600' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <Logo size="md" showText={true} textColor="white" />
              <div className="border-l border-white border-opacity-30 pl-6">
                <p className="text-sm text-white text-opacity-90">
                  Welcome back, {user?.name}
                  {user?.parentUserId && <span className="ml-2 text-xs bg-white bg-opacity-20 text-white px-2 py-1 rounded">Team Member</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {getRoleBadge(user?.role)}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="bg-white text-orange-600 hover:bg-white hover:bg-opacity-90" size="sm" data-testid="user-menu-button">
                    <User className="h-4 w-4 mr-2" />
                    {user?.name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowProfileDialog(true)} data-testid="profile-menu-item">
                    <Settings className="h-4 w-4 mr-2" />
                    Profile Settings
                  </DropdownMenuItem>
                  
                  {/* Company Menu Items (for users with companyId) */}
                  {user?.companyId && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setShowCompanyDialog(true)} data-testid="company-menu-item">
                        <Building2 className="h-4 w-4 mr-2" />
                        Company Settings
                      </DropdownMenuItem>
                      {user?.role === 'admin' && !user?.isSuperAdmin && (
                        <DropdownMenuItem onClick={() => navigate('/company/users')} data-testid="manage-team-menu-item">
                          <Users className="h-4 w-4 mr-2" />
                          Manage Team
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                  
                  {/* Super Admin Menu Items (super admin only - admin@specard.net) */}
                  {user?.role === 'admin' && user?.email === 'admin@specard.net' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-console-menu-item">
                        <Shield className="h-4 w-4 mr-2" />
                        Admin Console
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => logout(navigate)} data-testid="logout-menu-item">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Email Verification Banner */}
      {user && user.emailVerified === false && !user.isSuperAdmin && (
        <div className="bg-amber-50 border-b border-amber-200" data-testid="email-verify-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                <span className="font-medium">Please verify your email address to access projects.</span>{' '}
                Project cards stay visible, but opening or editing a project is blocked until your email is verified.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-100 flex-shrink-0"
              onClick={handleResendVerification}
              disabled={resendingVerify}
              data-testid="resend-verify-btn"
            >
              <Mail className="h-4 w-4 mr-1.5" />
              {resendingVerify ? 'Sending...' : 'Resend verification email'}
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-main">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-slate-900">Projects</h2>
            <p className="text-slate-600 mt-1">Manage your specifications</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-[10px] bg-slate-100 rounded-lg p-1" data-testid="view-switcher">
              <button
                onClick={() => { setViewMode('list'); localStorage.setItem('specard_view_mode', 'list'); }}
                className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-orange-500' : 'text-slate-400 hover:text-slate-600'}`}
                title="List View"
                data-testid="view-list"
              >
                <List className="h-[18px] w-[18px]" />
              </button>
              <button
                onClick={() => { setViewMode('comfortable'); localStorage.setItem('specard_view_mode', 'comfortable'); }}
                className={`p-1.5 rounded transition-colors ${viewMode === 'comfortable' ? 'bg-white shadow-sm text-orange-500' : 'text-slate-400 hover:text-slate-600'}`}
                title="Grid (2 columns)"
                data-testid="view-comfortable"
              >
                <Grid2x2 className="h-[18px] w-[18px]" />
              </button>
              <button
                onClick={() => { setViewMode('standard'); localStorage.setItem('specard_view_mode', 'standard'); }}
                className={`p-1.5 rounded transition-colors ${viewMode === 'standard' ? 'bg-white shadow-sm text-orange-500' : 'text-slate-400 hover:text-slate-600'}`}
                title="Grid (3 columns)"
                data-testid="view-standard"
              >
                <Grid3x3 className="h-[18px] w-[18px]" />
              </button>
            </div>
            {user?.role !== 'readonly' && user?.role !== 'owner' && (
              <Button
                onClick={async () => {
                  if (!(await ensureVerifiedForProjectCreate())) return;
                  setShowCreateDialog(true);
                }}
                data-testid="create-project-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Project
              </Button>
            )}
          </div>
        </div>

        {/* Create Project Dialog - Always render so it works from "No projects yet" card */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Project</DialogTitle>
                  <DialogDescription>
                    Add a new window project to manage specifications and QR codes
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="project-name">Project Name</Label>
                    <Input
                      id="project-name"
                      data-testid="project-name-input"
                      placeholder="Downtown Tower"
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="project-address">Address</Label>
                    <Input
                      id="project-address"
                      data-testid="project-address-input"
                      placeholder="123 Main St, City, State"
                      value={newProject.address}
                      onChange={(e) => setNewProject({ ...newProject, address: e.target.value })}
                      required
                    />
                  </div>
                  
                  {/* Project Image Upload */}
                  <div className="space-y-2">
                    <Label>Project Image (Optional)</Label>
                    <div className="space-y-3">
                      {projectImagePreview ? (
                        <div className="relative">
                          <img 
                            src={projectImagePreview} 
                            alt="Project preview" 
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={removeProjectImage}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label
                          className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                            dragOver
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-300 hover:border-orange-500 hover:bg-orange-50'
                          }`}
                          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                          onDrop={handleProjectImageDrop}
                          onPaste={handleProjectImagePaste}
                          tabIndex={0}
                          data-testid="project-image-upload-area"
                        >
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold">Click to upload</span>, drag & drop or paste an image
                            </p>
                            <p className="text-xs text-gray-500 mt-1">JPG, PNG or PDF (max 5MB)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            ref={projectImageInputRef}
                            accept="image/jpeg,image/png,image/jpg,application/pdf"
                            onChange={handleProjectImageSelect}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  <Button type="submit" className="w-full" data-testid="submit-project-button" disabled={creatingProject}>
                    {creatingProject ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Project'
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

        {/* Image Crop Modal */}
        <ImageCropModal
          open={showCropModal}
          onClose={handleCropModalClose}
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          aspectRatio={4 / 3}
        />

        {/* PDF Page Selector Modal */}
        <PDFPageSelector
          open={showPDFSelector}
          onClose={() => setShowPDFSelector(false)}
          pdfFile={projectImageFile}
          onPageSelect={handlePDFPageSelect}
        />

        {loading ? (
          <ProjectCardSkeletonGrid count={6} />
        ) : (
          <>
            {/* Company Projects Section */}
            <div className="mb-8">
              {/* Show "Company Projects" title if there are shared projects OR if no owned projects */}
              {(allSharedProjects.length > 0 || allOwnedProjects.length === 0) && (
                <h2 className="text-2xl font-bold text-slate-900 mb-4" data-testid="company-projects-title">Company Projects</h2>
              )}

              {/* Search + Sort + Create Project toolbar for Company Projects */}
              {allOwnedProjects.length > 0 && (
                <div className="flex items-center gap-3 mb-4" data-testid="company-projects-controls">
                  <Input
                    type="text"
                    placeholder="Search company projects..."
                    value={companyProjectSearch}
                    onChange={(e) => setCompanyProjectSearch(e.target.value)}
                    className="max-w-xs"
                    data-testid="company-project-search"
                  />
                  <Select value={companyProjectSort} onValueChange={setCompanyProjectSort}>
                    <SelectTrigger className="w-[200px]" data-testid="company-project-sort">
                      <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                      <SelectValue placeholder="Order by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name_asc">Name (A → Z)</SelectItem>
                      <SelectItem value="name_desc">Name (Z → A)</SelectItem>
                      <SelectItem value="created_desc">Created (Newest)</SelectItem>
                      <SelectItem value="created_asc">Created (Oldest)</SelectItem>
                      <SelectItem value="updated_desc">Updated (Newest)</SelectItem>
                      <SelectItem value="updated_asc">Updated (Oldest)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Show "No projects yet" card if no owned projects */}
              {allOwnedProjects.length === 0 && (
                <Card className="text-center py-6 mb-8">
                  <CardContent>
                    <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No projects yet</h3>
                    {user?.role !== 'owner' ? (
                      <>
                        <p className="text-slate-600 mb-4">Create your first project to get started</p>
                        <Button
                          onClick={async () => {
                            if (!(await ensureVerifiedForProjectCreate())) return;
                            setShowCreateDialog(true);
                          }}
                          disabled={creatingProject}
                          data-testid="create-first-project-button"
                        >
                          {creatingProject ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4 mr-2" />
                              Create Project
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <p className="text-slate-600">Contact your administrator to get assigned to projects</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* No search results for company projects */}
              {allOwnedProjects.length > 0 && ownedProjects.length === 0 && companyProjectSearch && (
                <Card className="text-center py-6">
                  <CardContent>
                    <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No projects found</h3>
                    <p className="text-slate-600">Try a different search term</p>
                  </CardContent>
                </Card>
              )}
              
              {ownedProjects.length > 0 && (
                <div className={`grid gap-6 ${viewMode === 'list' ? 'grid-cols-1' : viewMode === 'comfortable' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                  {ownedProjects.map((project) => {
              const canManage = project.createdBy === user?.id || 
                                (project.companyId && user?.companyId && project.companyId === user.companyId);
              const projectImage = getImageUrl(project, 'medium');
              const creatorName = project.createdBy === user?.id 
                ? 'You' 
                : project.users?.find(u => u.userId === project.createdBy)?.name || 'Unknown';
              
              return (
              <Card
                key={project.id}
                className={`hover:shadow-lg transition-all border-slate-200 relative overflow-hidden cursor-pointer ${viewMode === 'list' ? 'h-[100px]' : 'h-[160px]'}`}
                data-testid={`project-card-${project.id}`}
                onClick={() => openProject(project.id)}
              >
                {/* Delete button - top right */}
                {canManage && user?.role !== 'readonly' && (
                  <button
                    onClick={(e) => confirmDeleteProject(project, e)}
                    className="absolute top-2 right-2 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg"
                    title="Delete project"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                
                <div className="flex h-full">
                  <div className={`flex-shrink-0 relative ${viewMode === 'list' ? 'w-[140px]' : 'w-1/3'}`}>
                    <img 
                      src={projectImage}
                      alt={project.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = getProjectPlaceholderUrl(API);
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10"></div>
                  </div>
                  
                  <div className={`flex flex-col ${viewMode === 'list' ? 'flex-1' : 'w-2/3'}`}>
                    <CardHeader className="pb-1 pl-3 pr-4 pt-3">
                      <div className="flex items-start gap-2">
                        <Building2 className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <CardTitle className="line-clamp-2 text-sm">{project.name}</CardTitle>
                        </div>
                      </div>
                      <CardDescription className="flex items-start gap-2 mt-1">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-gray-500" />
                        <span className="line-clamp-1 text-xs">{project.address}</span>
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="pt-0 mt-auto pl-3 pr-4 pb-2.5">
                      <div className="grid grid-cols-3 gap-x-2 border-t pt-1.5" data-testid={`project-footer-${project.id}`}>
                        <span className="text-[9px] uppercase tracking-wide text-slate-400 font-medium">Created by</span>
                        <span className="text-[9px] uppercase tracking-wide text-slate-400 font-medium">Created at</span>
                        <span className="text-[9px] uppercase tracking-wide text-slate-400 font-medium">Updated at</span>
                        <span className="text-[11px] text-slate-600 truncate" title={creatorName}>{creatorName}</span>
                        <span className="text-[11px] text-slate-500">{new Date(project.createdAt).toLocaleDateString()}</span>
                        <span className="text-[11px] text-slate-500">{project.updatedAt ? new Date(project.updatedAt).toLocaleDateString() : '-'}</span>
                      </div>
                    </CardContent>
                  </div>
                </div>
              </Card>
              );
            })}
                </div>
              )}
            </div>

            {/* Shared Projects Section */}
            {allSharedProjects.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4" data-testid="shared-projects-title">Shared with You</h2>
                
                {/* Search + Sort + Company Filter */}
                <div className="flex flex-wrap items-center justify-start gap-3 mb-4" data-testid="shared-projects-controls">
                  <Input
                    type="text"
                    placeholder="Search shared projects..."
                    value={sharedProjectSearch}
                    onChange={(e) => setSharedProjectSearch(e.target.value)}
                    className="max-w-xs"
                    data-testid="shared-project-search"
                  />
                  <Select value={sharedProjectSort} onValueChange={setSharedProjectSort}>
                    <SelectTrigger className="w-[200px]" data-testid="shared-project-sort">
                      <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                      <SelectValue placeholder="Order by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name_asc">Name (A → Z)</SelectItem>
                      <SelectItem value="name_desc">Name (Z → A)</SelectItem>
                      <SelectItem value="created_desc">Created (Newest)</SelectItem>
                      <SelectItem value="created_asc">Created (Oldest)</SelectItem>
                      <SelectItem value="updated_desc">Updated (Newest)</SelectItem>
                      <SelectItem value="updated_asc">Updated (Oldest)</SelectItem>
                    </SelectContent>
                  </Select>
                  {sharedProjectCompanies.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-slate-500" />
                      <Select value={sharedProjectFilter} onValueChange={setSharedProjectFilter}>
                        <SelectTrigger className="w-[200px]" data-testid="shared-project-company-filter">
                          <SelectValue placeholder="Filter by company" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Companies</SelectItem>
                          {sharedProjectCompanies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* No shared results */}
                {filteredSharedProjects.length === 0 && (sharedProjectSearch || sharedProjectFilter !== 'all') && (
                  <Card className="text-center py-6">
                    <CardContent>
                      <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">No shared projects found</h3>
                      <p className="text-slate-600">Try a different search term or filter</p>
                    </CardContent>
                  </Card>
                )}
                
                {filteredSharedProjects.length > 0 && (
                <div className={`grid gap-6 ${viewMode === 'list' ? 'grid-cols-1' : viewMode === 'comfortable' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                  {filteredSharedProjects.map((project) => {
              const projectImage = getImageUrl(project, 'medium');
              const creatorName = project.createdBy === user?.id 
                ? 'You' 
                : project.users?.find(u => u.userId === project.createdBy)?.name || 'Unknown';
              
              return (
              <Card
                key={project.id}
                className={`hover:shadow-lg transition-all border-slate-200 relative overflow-hidden cursor-pointer ${viewMode === 'list' ? 'h-[100px]' : 'h-[160px]'}`}
                data-testid={`project-card-${project.id}`}
                onClick={() => openProject(project.id)}
              >
                {/* Shared Badge - top right */}
                <div className="absolute top-2 right-2 z-10 bg-blue-500 text-white rounded-full p-1.5 shadow-md" title="Shared with you">
                  <Users className="h-3.5 w-3.5" />
                </div>
                
                <div className="flex h-full">
                  <div className={`flex-shrink-0 relative ${viewMode === 'list' ? 'w-[140px]' : 'w-1/3'}`}>
                    <img 
                      src={projectImage}
                      alt={project.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = getProjectPlaceholderUrl(API);
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10"></div>
                  </div>
                  
                  <div className="w-2/3 flex flex-col">
                    <CardHeader className="pb-1 pl-3 pr-4 pt-3">
                      <div className="flex items-start gap-2">
                        <Building2 className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <CardTitle className="line-clamp-2 text-sm">{project.name}</CardTitle>
                        </div>
                      </div>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                        <span className="line-clamp-1 text-xs flex-1">{project.address}</span>
                        <span className="text-[11px] text-blue-600 font-medium flex-shrink-0 ml-auto" data-testid={`shared-company-${project.id}`}>
                          {project.companyName || 'Unknown'}
                        </span>
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="pt-0 mt-auto pl-3 pr-4 pb-2.5">
                      <div className="grid grid-cols-3 gap-x-2 border-t pt-1.5" data-testid={`project-footer-${project.id}`}>
                        <span className="text-[9px] uppercase tracking-wide text-slate-400 font-medium">Created by</span>
                        <span className="text-[9px] uppercase tracking-wide text-slate-400 font-medium">Created at</span>
                        <span className="text-[9px] uppercase tracking-wide text-slate-400 font-medium">Updated at</span>
                        <span className="text-[11px] text-slate-600 truncate" title={creatorName}>{creatorName}</span>
                        <span className="text-[11px] text-slate-500">{new Date(project.createdAt).toLocaleDateString()}</span>
                        <span className="text-[11px] text-slate-500">{project.updatedAt ? new Date(project.updatedAt).toLocaleDateString() : '-'}</span>
                      </div>
                    </CardContent>
                  </div>
                </div>
              </Card>
              );
            })}
                </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Profile Settings Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Profile Settings</DialogTitle>
            <DialogDescription>Update your account information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateProfile} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  required
                  data-testid="profile-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  required
                  data-testid="profile-lastname-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                required
                data-testid="profile-email-input"
              />
            </div>
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3">Change Password (Optional)</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <PasswordInput
                    value={profileData.currentPassword}
                    onChange={(e) => setProfileData({ ...profileData, currentPassword: e.target.value })}
                    placeholder="Leave blank to keep current password"
                    data-testid="profile-current-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <PasswordInput
                    value={profileData.newPassword}
                    onChange={(e) => setProfileData({ ...profileData, newPassword: e.target.value })}
                    placeholder="Leave blank to keep current password"
                    data-testid="profile-new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <PasswordInput
                    value={profileData.confirmNewPassword}
                    onChange={(e) => setProfileData({ ...profileData, confirmNewPassword: e.target.value })}
                    placeholder="Re-enter new password"
                    data-testid="profile-confirm-new-password"
                  />
                  {profileData.newPassword && profileData.confirmNewPassword && profileData.newPassword !== profileData.confirmNewPassword && (
                    <p className="text-sm text-red-500" data-testid="profile-password-mismatch-error">Passwords do not match</p>
                  )}
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full" data-testid="update-profile-button">
              Update Profile
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Company Settings Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Company Settings</DialogTitle>
            <DialogDescription>Update your company information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCompany} className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  required
                  value={companyData.companyName}
                  onChange={(e) => setCompanyData({ ...companyData, companyName: e.target.value })}
                  placeholder="Company name"
                />
              </div>
              <div className="space-y-2">
                <Label>Company Type *</Label>
                <select
                  required
                  value={companyData.companyType}
                  onChange={(e) => setCompanyData({ ...companyData, companyType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select type</option>
                  <option value="General Contractor">General Contractor</option>
                  <option value="Sub Contractor">Sub Contractor</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                rows={3}
                value={companyData.description}
                onChange={(e) => setCompanyData({ ...companyData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Company description"
              />
            </div>

            {/* Logo Upload */}
            <div className="space-y-2">
              <Label>Company Logo</Label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                </div>
                {logoPreview && (
                  <div className="w-32 h-32 border-2 border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                    <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                  </div>
                )}
              </div>
            </div>

            {/* Headquarters Address */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-700 mb-4">Headquarters Address</h4>
              <div className="space-y-4">
                <Input
                  type="text"
                  placeholder="Street Address"
                  value={companyData.headquartersAddress?.street || ''}
                  onChange={(e) => setCompanyData({
                    ...companyData,
                    headquartersAddress: { ...companyData.headquartersAddress, street: e.target.value }
                  })}
                />
                <div className="grid grid-cols-2 gap-4">
                  <select
                    value={companyData.headquartersAddress?.state || ''}
                    onChange={(e) => {
                      const state = e.target.value;
                      setCompanyData({
                        ...companyData,
                        headquartersAddress: { ...companyData.headquartersAddress, state, city: '' }
                      });
                      if (state) loadCities(state);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Select state</option>
                    {states.map(state => (
                      <option key={state.code} value={state.code}>{state.name}</option>
                    ))}
                  </select>
                  <select
                    value={companyData.headquartersAddress?.city || ''}
                    onChange={(e) => setCompanyData({
                      ...companyData,
                      headquartersAddress: { ...companyData.headquartersAddress, city: e.target.value }
                    })}
                    disabled={!companyData.headquartersAddress?.state}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
                  >
                    <option value="">Select city</option>
                    {cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <Input
                  type="text"
                  placeholder="ZIP Code"
                  value={companyData.headquartersAddress?.zipCode || ''}
                  onChange={(e) => setCompanyData({
                    ...companyData,
                    headquartersAddress: { ...companyData.headquartersAddress, zipCode: e.target.value }
                  })}
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={companyData.email}
                  onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                  placeholder="company@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  type="url"
                  value={companyData.website}
                  onChange={(e) => setCompanyData({ ...companyData, website: e.target.value })}
                  placeholder="https://company.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={companyData.phone}
                  onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label>Fax</Label>
                <Input
                  type="tel"
                  value={companyData.fax}
                  onChange={(e) => setCompanyData({ ...companyData, fax: e.target.value })}
                  placeholder="(555) 123-4568"
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCompanyDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1">Update Company Settings</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteProjectDialog}
        onClose={() => {
          setShowDeleteProjectDialog(false);
          setProjectToDelete(null);
        }}
        onConfirm={handleDeleteProject}
        type="danger"
        title="Delete Project"
        message={projectToDelete ? `Are you sure you want to delete "${projectToDelete.name}"? This action cannot be undone and will delete all window types, window list items, elevations, and markers associated with this project.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Remove Team Member Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveUserModal}
        onClose={() => setShowRemoveUserModal(false)}
        onConfirm={confirmRemoveUser}
        type="danger"
        title="Remove User"
        message={userToRemove ? `Are you sure you want to remove "${userToRemove.name || userToRemove.email}" from your team? This action cannot be undone.` : ''}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Dashboard;